import { Chip as MuiChip, type ChipProps as MuiChipProps } from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import { type AnchorHTMLAttributes, forwardRef, type SyntheticEvent } from 'react';

import type { WithoutStyleOverrides } from '../../../types/WithoutStyleOverrides';

const ROOT_ELEMENT = 'span';
const ANCHOR_ELEMENT = 'a';

type MuiRootChipProps = MuiChipProps<typeof ROOT_ELEMENT>;
type ClickHandler = NonNullable<MuiRootChipProps['onClick']>;
type DeleteHandler = NonNullable<MuiRootChipProps['onDelete']>;

type ChipAction
  = | { onClick: ClickHandler; href?: string; onDelete?: DeleteHandler }
    | { href: string; onClick?: ClickHandler; onDelete?: DeleteHandler }
    | { onDelete: DeleteHandler; onClick?: ClickHandler; href?: string };

type Props = WithoutStyleOverrides<Omit<MuiRootChipProps, 'ref' | 'slots' | 'slotProps' | 'onClick' | 'onDelete'>>
  & Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel' | 'download'>
  & ChipAction;

// forwardRef, not a `ref` prop: the app still runs React 18, where function components never receive `ref`.
export const Chip = forwardRef<HTMLElement, Props>(function Chip(props, ref) {
  const elementRef = useForkRef(ref);
  const { href, target, rel, download, onDelete, ...chipProps } = props;
  const deleteHandler = onDelete === undefined ? undefined : handleDelete;

  function handleDelete(event: SyntheticEvent) {
    event.preventDefault();
    if (props.disabled === true) {
      return;
    }
    onDelete?.(event);
  }

  if (href === undefined) {
    return <MuiChip {...chipProps} onDelete={deleteHandler} component={ROOT_ELEMENT} ref={elementRef} />;
  }

  return (
    <MuiChip
      {...chipProps}
      clickable
      onDelete={deleteHandler}
      component={ANCHOR_ELEMENT}
      href={href}
      target={target}
      rel={rel}
      download={download}
      ref={elementRef}
    />
  );
});

export type { Props as ChipProps };
